import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Download, FileSpreadsheet, Clock, User, Layers,
  CheckCircle2, XCircle, AlertTriangle, FileText, Loader2,
  ChevronDown, ChevronRight, ArrowRight, Database
} from "lucide-react";

function StatCard({ label, value, color = "text-white/70", icon: Icon }) {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {Icon && <Icon className={`w-4 h-4 mx-auto mb-1 ${color}`} />}
      <p className={`text-2xl font-semibold font-mono ${color}`}>{value}</p>
      <p className="text-white/25 text-[10px] uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

export default function ImportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSkips, setShowSkips] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [showMapping, setShowMapping] = useState(true);
  const [showPreview, setShowPreview] = useState(true);
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchBatch = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/imports/${id}`, { headers: h });
        setBatch(res.data);
      } catch (e) {
        toast.error("Import nicht gefunden");
        navigate("/imports");
      } finally {
        setLoading(false);
      }
    };
    if (token && id) fetchBatch();
  }, [id, token]);

  const downloadFile = async (type) => {
    try {
      const res = await axios.get(`${API}/imports/${id}/download/${type}`, {
        headers: h, responseType: "blob"
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, "") || `${type}_${id}`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      toast.success(`${type} downloaded`);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "File not available" : "Download failed");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" />
    </div>
  );

  if (!batch) return null;

  const mapped = batch.mapped_columns || {};
  const unmapped = batch.unmapped_columns || [];
  const preview = batch.preview_rows || [];
  const skipReasons = batch.skip_reasons || [];
  const errorDetails = batch.error_details || [];
  const parseInfo = batch.parse_info || {};

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="import-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate("/imports")} className="p-2 text-white/30 hover:text-white/60 hover:bg-white/5 rounded-lg transition-colors mt-1" data-testid="back-to-imports">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold text-white" data-testid="import-detail-title">{batch.batch_name}</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-white/30">
            <span className="flex items-center gap-1"><FileSpreadsheet className="w-3 h-3" />{batch.file_name}</span>
            <span className="text-white/10">|</span>
            <span className="flex items-center gap-1"><Layers className="w-3 h-3" />{batch.file_type?.toUpperCase()}</span>
            <span className="text-white/10">|</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(batch.upload_date).toLocaleString("de-DE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="text-white/10">|</span>
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{batch.uploaded_by}</span>
            {batch.source_name && (
              <>
                <span className="text-white/10">|</span>
                <span className="flex items-center gap-1"><Database className="w-3 h-3" />{batch.source_name}</span>
              </>
            )}
          </div>
          {batch.derived_consortium && (
            <p className="text-amber-400/60 text-xs mt-1">Consortium: {batch.derived_consortium}</p>
          )}
        </div>
        <span className={`text-[10px] font-semibold px-2.5 py-1 rounded uppercase tracking-wider ${batch.processing_status === "completed" ? "bg-green-500/10 text-green-400/80" : batch.processing_status === "archived" ? "bg-white/5 text-white/30" : "bg-amber-500/10 text-amber-400/60"}`} data-testid="import-status">
          {batch.processing_status}
        </span>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Total Rows" value={batch.rows_detected} icon={FileText} />
        <StatCard label="New" value={batch.imported} color="text-green-400/80" icon={CheckCircle2} />
        <StatCard label="Merged" value={batch.merged} color="text-blue-400/80" icon={ArrowRight} />
        <StatCard label="Skipped" value={batch.skipped} color="text-amber-400/80" icon={AlertTriangle} />
        <StatCard label="Errors" value={batch.errors} color={batch.errors > 0 ? "text-red-400/80" : "text-white/30"} icon={XCircle} />
      </div>

      {/* Parse Info */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
        <p className="text-white/30 text-[10px] uppercase tracking-wider mb-2">Parse Configuration</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <div><span className="text-white/25">Format</span><p className="text-white/60 font-mono">{parseInfo.format?.toUpperCase()}</p></div>
          {parseInfo.sheet_name && <div><span className="text-white/25">Sheet</span><p className="text-white/60 font-mono">{parseInfo.sheet_name}</p></div>}
          <div><span className="text-white/25">Header Row</span><p className="text-white/60 font-mono">Row {(parseInfo.header_row_index || 0) + 1}</p></div>
          <div><span className="text-white/25">Raw Rows</span><p className="text-white/60 font-mono">{parseInfo.total_raw_rows}</p></div>
        </div>
      </div>

      {/* Downloads */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
        <p className="text-white/30 text-[10px] uppercase tracking-wider mb-3">Downloads</p>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => downloadFile("original")} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] text-white/50 text-xs rounded-lg hover:bg-white/[0.08] hover:text-white/70 transition-colors" data-testid="download-original">
            <Download className="w-3.5 h-3.5" /> Original File
          </button>
          <button onClick={() => downloadFile("parsed-data")} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] text-white/50 text-xs rounded-lg hover:bg-white/[0.08] hover:text-white/70 transition-colors" data-testid="download-parsed">
            <Download className="w-3.5 h-3.5" /> Parsed Data
          </button>
          <button onClick={() => downloadFile("skip-report")} disabled={batch.skipped === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] text-white/50 text-xs rounded-lg hover:bg-white/[0.08] hover:text-white/70 transition-colors disabled:opacity-20 disabled:cursor-not-allowed" data-testid="download-skip">
            <Download className="w-3.5 h-3.5" /> Skip Report
          </button>
          <button onClick={() => downloadFile("error-report")} disabled={batch.errors === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] text-white/50 text-xs rounded-lg hover:bg-white/[0.08] hover:text-white/70 transition-colors disabled:opacity-20 disabled:cursor-not-allowed" data-testid="download-errors">
            <Download className="w-3.5 h-3.5" /> Error Report
          </button>
        </div>
      </div>

      {/* Column Mapping */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <button onClick={() => setShowMapping(!showMapping)} className="w-full flex items-center justify-between p-4 text-left" data-testid="toggle-mapping">
          <p className="text-white/30 text-[10px] uppercase tracking-wider">Column Mapping ({Object.keys(mapped).length} mapped, {unmapped.length} unmapped)</p>
          {showMapping ? <ChevronDown className="w-4 h-4 text-white/20" /> : <ChevronRight className="w-4 h-4 text-white/20" />}
        </button>
        {showMapping && (
          <div className="px-4 pb-4">
            {Object.keys(mapped).length > 0 && (
              <div className="mb-3">
                <p className="text-green-400/50 text-[10px] uppercase tracking-wider mb-1.5">Mapped</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {Object.entries(mapped).map(([src, target]) => (
                    <div key={src} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="text-white/40 truncate flex-1">{src}</span>
                      <ArrowRight className="w-3 h-3 text-green-400/30 shrink-0" />
                      <span className="text-green-400/60 font-mono truncate flex-1">{target}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {unmapped.length > 0 && (
              <div>
                <p className="text-amber-400/50 text-[10px] uppercase tracking-wider mb-1.5">Unmapped (stored as extra attributes)</p>
                <div className="flex flex-wrap gap-1.5">
                  {unmapped.map(col => (
                    <span key={col} className="text-[10px] px-2 py-0.5 rounded bg-amber-500/10 text-amber-400/50">{col}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Rows */}
      {preview.length > 0 && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
          <button onClick={() => setShowPreview(!showPreview)} className="w-full flex items-center justify-between p-4 text-left" data-testid="toggle-preview">
            <p className="text-white/30 text-[10px] uppercase tracking-wider">Data Preview ({preview.length} rows)</p>
            {showPreview ? <ChevronDown className="w-4 h-4 text-white/20" /> : <ChevronRight className="w-4 h-4 text-white/20" />}
          </button>
          {showPreview && (
            <div className="px-4 pb-4 space-y-3">
              {preview.map((row, i) => (
                <div key={i} className="bg-white/[0.02] rounded-lg border border-white/[0.04] p-3">
                  <p className="text-white/25 text-[10px] font-mono mb-1.5">Row {row.row}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                    {Object.entries(row.mapped || {}).map(([key, val]) => (
                      <div key={key} className="flex justify-between gap-2 overflow-hidden">
                        <span className="text-white/25 truncate">{key}</span>
                        <span className="text-white/60 font-mono truncate text-right">{val}</span>
                      </div>
                    ))}
                  </div>
                  {row.extra_keys?.length > 0 && (
                    <p className="text-white/15 text-[10px] mt-1">+ {row.extra_keys.length} extra: {row.extra_keys.join(", ")}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Skip Reasons */}
      {skipReasons.length > 0 && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
          <button onClick={() => setShowSkips(!showSkips)} className="w-full flex items-center justify-between p-4 text-left" data-testid="toggle-skips">
            <p className="text-amber-400/50 text-[10px] uppercase tracking-wider">Skip Reasons ({skipReasons.length})</p>
            {showSkips ? <ChevronDown className="w-4 h-4 text-white/20" /> : <ChevronRight className="w-4 h-4 text-white/20" />}
          </button>
          {showSkips && (
            <div className="px-4 pb-4 space-y-1">
              {skipReasons.map((reason, i) => (
                <div key={i} className="flex items-start gap-2 text-xs py-0.5">
                  <AlertTriangle className="w-3 h-3 text-amber-400/40 shrink-0 mt-0.5" />
                  <span className="text-white/40">{reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error Details */}
      {errorDetails.length > 0 && (
        <div className="bg-white/[0.02] border border-red-500/10 rounded-xl overflow-hidden">
          <button onClick={() => setShowErrors(!showErrors)} className="w-full flex items-center justify-between p-4 text-left" data-testid="toggle-errors">
            <p className="text-red-400/50 text-[10px] uppercase tracking-wider">Error Details ({errorDetails.length})</p>
            {showErrors ? <ChevronDown className="w-4 h-4 text-white/20" /> : <ChevronRight className="w-4 h-4 text-white/20" />}
          </button>
          {showErrors && (
            <div className="px-4 pb-4 space-y-1">
              {errorDetails.map((err, i) => (
                <div key={i} className="flex items-start gap-2 text-xs py-0.5">
                  <XCircle className="w-3 h-3 text-red-400/40 shrink-0 mt-0.5" />
                  <span className="text-white/40">{err}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
